/**
 * Outil « Simulateur de lois » (Boîte à outils, 1re → prépa).
 *
 * Monty Hall à n portes : on joue une partie (choisir, l'animateur ouvre les
 * portes perdantes, garder ou changer), puis on simule 1 000 parties de
 * chaque stratégie : garder gagne 1/n, changer (n − 1)/n (l'explication vient
 * après l'expérience). Loi géométrique (Tle maths complémentaires, MP/PT) :
 * séries d'épreuves jusqu'au premier succès, histogramme contre
 * P(X = k) = (1 − p)^(k−1) p, moyenne contre 1/p, absence de mémoire. Loi de
 * Poisson (MP/PT) : P(λ) contre B(n ; λ/n). MPSI/PTSI (univers finis) : Monty
 * Hall seul. Défi par thème selon le niveau. Logique : `laws.ts` ; tirages :
 * `rng` (tests reproductibles).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { cn } from '@jeraisonne/ui';
import {
  Note,
  ParamSlider,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { formatNumber, frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { AXIS, FIGURE_BG_CLASS, INK } from '../../ui/svg-parts';
import {
  THEME_KINDS,
  THEME_LABELS,
  addValues,
  binomPmf,
  count,
  countAbove,
  dec,
  drawGeom,
  drawSeries,
  emptySample,
  eqText,
  fracText,
  freq,
  geomMean,
  geomPmf,
  geomQuantile,
  geomTail,
  hostOpens,
  makeQuiz,
  maxGap,
  poissonMaxK,
  poissonPmf,
  quizThemes,
  simulateMonty,
  sup,
  type MontyRound,
  type QuizTheme,
  type Sample,
} from './laws';

const W = 320;
const H = 170;
const BAR = 'var(--color-primary-500)';
const LAW = 'var(--color-xp-500)';
const NNBSP = String.fromCharCode(0x202f);
const pct = (v: number) => `${formatNumber(v * 100, 1)}${NNBSP}%`;
const PAD = 14;
const plural = (n: number, w: string) => `${count(n)} ${w}${n > 1 ? 's' : ''}`;

/** légende sous un graphique : barres pleines, traits pointillés */
function Legend({ bar, dash }: { bar: string; dash: string }) {
  return (
    <p className="text-ink-700 flex flex-wrap gap-x-4 gap-y-1 px-1 pt-1 text-xs">
      <span className="flex items-center gap-1.5">
        <span aria-hidden="true" className="bg-primary-500/70 inline-block h-3 w-3 rounded-sm" />
        {frenchSpaces(bar)}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="border-xp-500 inline-block w-5 border-t-[2.5px] border-dashed"
        />
        {frenchSpaces(dash)}
      </span>
    </p>
  );
}

/** diagramme en bâtons : barres (fréquences ou loi) et traits pointillés (loi) */
function Bars({
  bars,
  marks,
  first,
  label,
  testId,
}: {
  bars: readonly number[];
  marks?: readonly number[];
  /** valeur de la première barre (0 pour Poisson, 1 pour la géométrique) */
  first: number;
  label: string;
  testId: string;
}) {
  const n = bars.length;
  const top = 10;
  const base = H - 22;
  const max = Math.max(0.05, ...bars, ...(marks ?? []));
  const dx = (W - 2 * PAD) / n;
  const x = (i: number) => PAD + i * dx;
  const y = (v: number) => base - (v / max) * (base - top);
  const every = n > 16 ? Math.ceil(n / 12) : 1;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
      role="img"
      aria-label={label}
      data-testid={testId}
    >
      <line x1={PAD} x2={W - PAD} y1={base} y2={base} stroke={AXIS} strokeWidth={1.25} />
      {bars.map((v, i) => (
        <g key={i}>
          <rect
            x={x(i) + dx * 0.12}
            y={y(v)}
            width={dx * 0.76}
            height={Math.max(0, base - y(v))}
            fill={BAR}
            fillOpacity={0.7}
            rx={2}
          />
          {marks && (
            <line
              x1={x(i) + dx * 0.05}
              x2={x(i) + dx * 0.95}
              y1={y(marks[i] ?? 0)}
              y2={y(marks[i] ?? 0)}
              stroke={LAW}
              strokeWidth={2.5}
              strokeDasharray="4 3"
            />
          )}
          {i % every === 0 && (
            <text x={x(i) + dx / 2} y={H - 7} textAnchor="middle" fontSize={12} fill={INK}>
              {first + i}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

/* ---------- Monty Hall ---------- */

type Tally = { games: number; wins: number };
const T0: Tally = { games: 0, wins: 0 };

function Door({
  i,
  state,
  onClick,
  disabled,
  mine = false,
}: {
  i: number;
  state: 'closed' | 'picked' | 'goat' | 'car' | 'goat-final';
  onClick?: () => void;
  disabled?: boolean;
  /** fin de partie : la porte finale du joueur */
  mine?: boolean;
}) {
  const said =
    state === 'goat'
      ? 'ouverte : chèvre'
      : state === 'car'
        ? 'voiture'
        : state === 'goat-final'
          ? 'chèvre'
          : state === 'picked'
            ? 'ton choix'
            : 'fermée';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-disabled={disabled || undefined}
      aria-label={`Porte ${i + 1} : ${said}${mine ? ', ta porte finale' : ''}`}
      className={cn(
        'flex min-h-16 flex-col items-center justify-center rounded-xl border-2 text-sm font-semibold',
        state === 'picked' ? 'border-primary-500 bg-primary-50' : 'border-ink-200',
        (state === 'goat' || state === 'goat-final') && 'bg-ink-100 text-ink-500',
        state === 'car' && 'border-success-600 bg-success-50',
        mine && 'ring-primary-500 ring-2 ring-offset-1',
      )}
    >
      <span aria-hidden="true" className="text-2xl leading-none">
        {state === 'goat' || state === 'goat-final' ? '🐐' : state === 'car' ? '🚗' : '🚪'}
      </span>
      <span aria-hidden="true">{i + 1}</span>
      {mine && (
        <span aria-hidden="true" className="text-ink-700 text-xs font-bold">
          ta porte
        </span>
      )}
    </button>
  );
}

function MontyPanel({ rng }: { rng: () => number }) {
  const [doors, setDoors] = useState(3);
  const [car, setCar] = useState(() => Math.floor(rng() * 3));
  const [round, setRound] = useState<MontyRound | null>(null);
  const [final, setFinal] = useState<number | null>(null);
  const [mine, setMine] = useState<{ keep: Tally; swap: Tally }>({ keep: T0, swap: T0 });
  const [sim, setSim] = useState<{ keep: Tally; swap: Tally }>({ keep: T0, swap: T0 });
  /** synthèse annoncée après une simulation */
  const [said, setSaid] = useState('');

  const restart = (n = doors) => {
    setCar(Math.min(n - 1, Math.floor(rng() * n)));
    setRound(null);
    setFinal(null);
  };
  const changeDoors = (n: number) => {
    setDoors(n);
    setMine({ keep: T0, swap: T0 });
    setSim({ keep: T0, swap: T0 });
    setSaid('');
    restart(n);
  };
  const choose = (i: number) => {
    if (round) return;
    setRound(hostOpens(doors, car, i, rng));
  };
  const decide = (swap: boolean) => {
    if (!round || final !== null) return;
    const f = swap ? round.other : round.pick;
    setFinal(f);
    const win = f === car;
    setMine((t) => {
      const k = swap ? 'swap' : 'keep';
      return { ...t, [k]: { games: t[k].games + 1, wins: t[k].wins + (win ? 1 : 0) } };
    });
  };
  const simulate = () => {
    const g = 1000;
    const k = simulateMonty(doors, false, g, rng);
    const s = simulateMonty(doors, true, g, rng);
    const next = {
      keep: { games: sim.keep.games + g, wins: sim.keep.wins + k },
      swap: { games: sim.swap.games + g, wins: sim.swap.wins + s },
    };
    setSim(next);
    setSaid(
      `${count(next.keep.games)} parties de chaque : en gardant ${pct(next.keep.wins / next.keep.games)} de victoires, en changeant ${pct(next.swap.wins / next.swap.games)}.`,
    );
  };

  const stateOf = (i: number): 'closed' | 'picked' | 'goat' | 'car' | 'goat-final' => {
    if (final !== null) return i === car ? 'car' : 'goat-final';
    if (!round) return 'closed';
    if (round.opened.includes(i)) return 'goat';
    return i === round.pick ? 'picked' : 'closed';
  };
  const status =
    final !== null
      ? final === car
        ? `Gagné : la voiture était derrière la porte ${car + 1}.`
        : `Perdu : la voiture était derrière la porte ${car + 1}.`
      : round
        ? `L’animateur a ouvert ${round.opened.length} porte${round.opened.length > 1 ? 's' : ''} perdante${round.opened.length > 1 ? 's' : ''}. Tu gardes la porte ${round.pick + 1} ou tu changes pour la porte ${round.other + 1} ?`
        : 'Choisis une porte : une voiture est cachée derrière l’une d’elles, des chèvres derrière les autres.';

  const played = mine.keep.games + mine.swap.games;
  const line = (label: string, t: Tally, th: string, thv: number) =>
    t.games === 0
      ? null
      : `${label} : ${count(t.wins)} victoire${t.wins > 1 ? 's' : ''} sur ${count(t.games)} (${pct(t.wins / t.games)}) — théorie ${th} ${Math.abs(Number((thv * 100).toFixed(1)) - thv * 100) < 1e-9 ? '=' : '≈'} ${pct(thv)}.`;

  return (
    <div className="space-y-3">
      <Stepper
        label="Nombre de portes"
        value={doors}
        min={3}
        max={10}
        onChange={changeDoors}
        format={(v) => `${v} portes`}
      />
      <div
        role="group"
        aria-label="Portes"
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${Math.min(doors, 5)}, minmax(0, 1fr))` }}
        data-testid="lh-doors"
      >
        {Array.from({ length: doors }, (_, i) => (
          <Door
            key={i}
            i={i}
            state={stateOf(i)}
            disabled={!!round}
            mine={final === i}
            onClick={() => choose(i)}
          />
        ))}
      </div>
      <p className="text-ink-800 text-sm" aria-live="polite" data-testid="lh-status">
        {frenchSpaces(status)}
      </p>
      {round && final === null && (
        <div className="flex flex-wrap gap-2">
          {/* deux boutons de même poids : l'outil ne souffle pas la bonne stratégie */}
          <button type="button" className={SECONDARY_BUTTON} onClick={() => decide(false)}>
            {`Garder la porte ${round.pick + 1}`}
          </button>
          <button type="button" className={SECONDARY_BUTTON} onClick={() => decide(true)}>
            {`Changer pour la porte ${round.other + 1}`}
          </button>
        </div>
      )}
      {final !== null && (
        <button type="button" className={PRIMARY_BUTTON} onClick={() => restart()}>
          Nouvelle partie
        </button>
      )}
      {played > 0 && (
        <p className="text-ink-600 text-xs" data-testid="lh-mine">
          {frenchSpaces(
            `Tes parties : ${[
              mine.keep.games > 0 &&
                `en gardant, ${plural(mine.keep.wins, 'victoire')} sur ${mine.keep.games}`,
              mine.swap.games > 0 &&
                `en changeant, ${plural(mine.swap.wins, 'victoire')} sur ${mine.swap.games}`,
            ]
              .filter(Boolean)
              .join(' ; ')}.`,
          )}
        </p>
      )}
      <div className="border-ink-100 space-y-2 rounded-2xl border p-3">
        <button type="button" className={SECONDARY_BUTTON} onClick={simulate}>
          {frenchSpaces(`Simuler ${count(1000)} parties de chaque`)}
        </button>
        <p className="sr-only" aria-live="polite">
          {frenchSpaces(said)}
        </p>
        {sim.keep.games > 0 && (
          <div className="space-y-2" data-testid="lh-sim">
            {(
              [
                ['Garder', sim.keep, fracText(1, doors), 1 / doors],
                ['Changer', sim.swap, fracText(doors - 1, doors), (doors - 1) / doors],
              ] as const
            ).map(([label, t, th, thv]) => (
              <div key={label} className="space-y-1">
                <p className="text-ink-800 text-sm">{frenchSpaces(line(label, t, th, thv)!)}</p>
                <div
                  className="bg-ink-100 relative h-3 overflow-hidden rounded-full"
                  aria-hidden="true"
                >
                  <div
                    className="bg-primary-500 h-full rounded-full"
                    style={{ width: `${(100 * t.wins) / t.games}%` }}
                  />
                  <div
                    className="bg-xp-500 absolute top-0 h-full w-0.5"
                    style={{ left: `${100 * thv}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* l'explication vient après l'expérience : une simulation ou trois parties */}
      {sim.keep.games > 0 || played >= 3 ? (
        <Note live={false} tone="neutral">
          {frenchSpaces(
            `Pourquoi ? En gardant, on gagne si le premier choix était la voiture : ${fracText(1, doors)}. En changeant, on gagne dans tous les autres cas, car l’animateur, qui sait, n’ouvre que des portes perdantes : ${fracText(doors - 1, doors)}.`,
          )}
        </Note>
      ) : (
        <p className="text-ink-500 text-xs">
          {frenchSpaces(
            'Joue quelques parties, ou lance la simulation : laquelle des deux stratégies gagne le plus souvent ?',
          )}
        </p>
      )}
    </div>
  );
}

/* ---------- loi géométrique ---------- */

/** « Une série : 2 échecs, puis un succès : X = 3. » */
const seriesText = (sr: readonly boolean[]) =>
  sr.length === 1
    ? 'Succès dès la première épreuve : X = 1.'
    : `${plural(sr.length - 1, 'échec')}, puis un succès : X = ${sr.length}.`;

function GeomPanel({ rng, tle }: { rng: () => number; tle: boolean }) {
  const [p, setP] = useState(0.2);
  const [s, setS] = useState<Sample>(emptySample);
  const [series, setSeries] = useState<boolean[] | null>(null);
  /** synthèse annoncée après chaque tirage */
  const [said, setSaid] = useState('');
  const [showMem, setShowMem] = useState(false);
  const [m, setM] = useState(3);
  const [n, setN] = useState(2);
  const K = Math.min(25, geomQuantile(p, 0.99));

  const changeP = (v: number) => {
    setP(v);
    setS(emptySample());
    setSeries(null);
    setSaid('');
  };
  const summary = (ns: Sample) => {
    const a = countAbove(ns, m);
    const mem =
      showMem && a >= 30
        ? ` Sachant X > ${m}, fréquence de X > ${m + n} : ${dec(countAbove(ns, m + n) / a, 3)}, contre (1 − p)${sup(n)} ${eqText(geomTail(p, n), 4)}.`
        : '';
    return `${plural(ns.total, 'série')} : moyenne des X ${eqText(ns.sum / ns.total, 2)}, contre 1/p ${eqText(geomMean(p), 2)}.${mem}`;
  };
  const one = () => {
    const sr = drawSeries(p, rng);
    const ns = addValues(s, [sr.length]);
    setSeries(sr);
    setS(ns);
    setSaid(`Une série : ${seriesText(sr)} ${summary(ns)}`);
  };
  const many = (k: number) => {
    const ns = addValues(
      s,
      Array.from({ length: k }, () => drawGeom(p, rng)),
    );
    setSeries(null);
    setS(ns);
    setSaid(`${plural(k, 'série')} de plus. ${summary(ns)}`);
  };
  const bars = Array.from({ length: K }, (_, i) => freq(s, i + 1));
  const law = Array.from({ length: K }, (_, i) => geomPmf(p, i + 1));
  const beyond = s.total ? countAbove(s, K) / s.total : 0;
  const above = countAbove(s, m);
  const aboveBoth = countAbove(s, m + n);

  return (
    <div className="space-y-3">
      <p className="text-ink-600 text-sm">
        {frenchSpaces(
          `On répète une épreuve de Bernoulli (succès avec la probabilité p) jusqu’au premier succès ; X est le rang de ce succès.${tle ? ' Au programme de maths complémentaires (temps d’attente).' : ''}`,
        )}
      </p>
      <ParamSlider
        name="p (succès)"
        ariaLabel="Probabilité p du succès"
        value={p}
        min={0.05}
        max={0.9}
        step={0.05}
        format={(v) => formatNumber(v, 2)}
        onChange={changeP}
      />
      <div className="flex flex-wrap gap-2" role="group" aria-label="Lancer des séries">
        <button type="button" className={PRIMARY_BUTTON} onClick={one}>
          Une série
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={() => many(100)}>
          100 séries
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={() => many(1000)}>
          {frenchSpaces(`${count(1000)} séries`)}
        </button>
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={() => {
            setS(emptySample());
            setSeries(null);
            setSaid('Séries effacées.');
          }}
        >
          Effacer
        </button>
      </div>
      {/* une région annoncée toujours montée : ses changements sont lus */}
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(said)}
      </p>
      {series && (
        <div className="space-y-1" data-testid="lg-series">
          <p aria-hidden="true" className="flex flex-wrap gap-1">
            {series.slice(0, 30).map((ok, i) => (
              <span
                key={i}
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold',
                  ok ? 'bg-success-600 text-white' : 'bg-ink-100 text-ink-600',
                )}
              >
                {ok ? 'S' : 'É'}
              </span>
            ))}
            {series.length > 30 && <span className="text-ink-600 text-sm">…</span>}
          </p>
          <p className="text-ink-800 text-sm">{frenchSpaces(seriesText(series))}</p>
        </div>
      )}
      <div>
        <Bars
          bars={bars}
          marks={law}
          first={1}
          testId="lg-bars"
          label={
            s.total === 0
              ? `Aucune série pour l’instant. Pointillés : loi géométrique de paramètre ${formatNumber(p, 2)}, de 1 à ${K}.`
              : `Fréquences des valeurs de X sur ${plural(s.total, 'série')}, moyenne ${dec(s.sum / s.total, 2)} (barres) ; loi géométrique de paramètre ${formatNumber(p, 2)}, P(X = 1) = ${dec(p, 2)} (pointillés), de 1 à ${K}.`
          }
        />
        <Legend bar="fréquences observées" dash={`loi G(${formatNumber(p, 2)})`} />
      </div>
      <p className="text-ink-500 text-xs">
        {frenchSpaces('S : succès, É : échec. Chaque série s’arrête au premier succès.')}
      </p>
      <div className="space-y-1 text-sm" data-testid="lg-stats">
        <p className="text-ink-900 font-semibold">
          {frenchSpaces(`P(X = k) = (1 − p)ᵏ⁻¹ × p ; E(X) = 1/p ${eqText(geomMean(p), 2)}.`)}
        </p>
        {s.total > 0 && (
          <p className="text-ink-800">
            {frenchSpaces(
              `${plural(s.total, 'série')} : moyenne des X ${eqText(s.sum / s.total, 2)}${beyond > 0 ? ` ; X > ${K} dans ${pct(beyond)} des cas (hors du graphique)` : ''}.`,
            )}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <ToggleChip on={showMem} onClick={() => setShowMem(!showMem)}>
          Absence de mémoire
        </ToggleChip>
        {showMem && (
          <div className="border-ink-100 space-y-2 rounded-2xl border p-3" data-testid="lg-memory">
            <p className="text-ink-600 text-sm">
              {frenchSpaces(
                'On a déjà fait m épreuves sans succès. Quelle chance d’attendre encore plus de n épreuves ?',
              )}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Stepper label="m" value={m} min={1} max={10} onChange={setM} />
              <Stepper label="n" value={n} min={1} max={10} onChange={setN} />
            </div>
            <p className="text-ink-800 text-sm">
              {frenchSpaces(
                above < 30
                  ? `Pas assez de séries avec X > ${m} (${above}) : lance d’autres séries, ou prends p ou m plus petit.`
                  : `Parmi les ${count(above)} séries où X > ${m}, ${count(aboveBoth)} ont X > ${m + n} : fréquence ${dec(aboveBoth / above, 3)}.`,
              )}
            </p>
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(
                `P(X > ${n}) = (1 − p)${sup(n)} ${eqText(geomTail(p, n), 4)} : avoir déjà attendu ${m} épreuves ne change rien.`,
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- loi de Poisson ---------- */

const NS = [5, 10, 20, 50, 100, 200] as const;
/** n permis pour λ : λ/n ≤ 1/2 (sinon la binomiale est dégénérée) */
const nsFor = (lambda: number) => NS.filter((n) => lambda / n <= 0.5);

function PoissonPanel() {
  const [lambda, setLambda] = useState(2);
  const [wanted, setWanted] = useState<number>(20);
  const ns = nsFor(lambda);
  const n = ns.includes(wanted as (typeof NS)[number])
    ? wanted
    : (ns.find((v) => v >= wanted) ?? ns[0]!);
  const ni = ns.indexOf(n as (typeof NS)[number]);
  const K = poissonMaxK(lambda);
  const law = Array.from({ length: K + 1 }, (_, k) => poissonPmf(lambda, k));
  const bin = Array.from({ length: K + 1 }, (_, k) => binomPmf(n, lambda / n, k));
  const gap = maxGap(lambda, n, K);
  return (
    <div className="space-y-3">
      <p className="text-ink-600 text-sm">
        {frenchSpaces(
          'Loi de Poisson P(λ) : P(X = k) = e^(−λ) λᵏ / k!. C’est la limite de la loi binomiale B(n ; λ/n) quand n grandit (beaucoup d’épreuves, succès rare).',
        )}
      </p>
      <ParamSlider
        name="λ"
        ariaLabel="Paramètre lambda"
        value={lambda}
        min={0.5}
        max={5}
        step={0.5}
        format={(v) => formatNumber(v, 1)}
        onChange={setLambda}
      />
      <Stepper
        label="Binomiale B(n ; λ/n)"
        value={ni}
        min={0}
        max={ns.length - 1}
        onChange={(i) => setWanted(ns[i]!)}
        format={(i) => `n = ${ns[i]}`}
      />
      <div>
        <Bars
          bars={law}
          marks={bin}
          first={0}
          testId="lp-bars"
          label={`Loi de Poisson de paramètre ${formatNumber(lambda, 1)} (barres) et loi binomiale B(${n} ; ${formatNumber(lambda / n, 3)}) (pointillés), de 0 à ${K}. Écart maximal ${dec(gap)}.`}
        />
        <Legend
          bar={`loi de Poisson P(${formatNumber(lambda, 1)})`}
          dash={`binomiale B(${n} ; ${formatNumber(lambda / n, 3)})`}
        />
      </div>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(`λ = ${formatNumber(lambda, 1)}, n = ${n} : écart maximal ${dec(gap)}.`)}
      </p>
      <div className="space-y-1 text-sm" data-testid="lp-stats">
        <p className="text-ink-900 font-semibold">
          {frenchSpaces(
            `E(X) = V(X) = λ = ${formatNumber(lambda, 1)} ; P(X = 0) = e^(−λ) ≈ ${dec(poissonPmf(lambda, 0))}.`,
          )}
        </p>
        <p className="text-ink-800">
          {frenchSpaces(
            `Écart maximal entre B(${n} ; λ/${n}) et P(λ) : ${dec(gap)}. Plus n est grand, plus il est petit.`,
          )}
        </p>
      </div>
    </div>
  );
}

/* ---------- défi ---------- */

function Challenge({ seed, themes }: { seed?: number; themes: readonly QuizTheme[] }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const draw = () => Math.floor(rnd() * 2 ** 31);
  const [theme, setTheme] = useState<QuizTheme>(themes[0]!);
  const kinds = THEME_KINDS[theme];
  const rounds = kinds.length;
  const [count1, setCount] = useState(1);
  const [qSeed, setQSeed] = useState(draw);
  const kind = kinds[count1 - 1]!;
  const q = useMemo(() => makeQuiz(kind, seededRng(qSeed)), [kind, qSeed]);
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count1 >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count1 + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQSeed(draw());
    setPicked(null);
  };
  const changeTheme = (t: QuizTheme) => {
    setTheme(t);
    setCount(1);
    setScore(0);
    setQSeed(draw());
    setPicked(null);
  };

  return (
    <div className="space-y-3">
      {themes.length > 1 && (
        <Segmented
          ariaLabel="Thème du défi"
          value={theme}
          options={themes.map((t) => ({ value: t, label: THEME_LABELS[t] }))}
          onChange={changeTheme}
        />
      )}
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count1} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(q.question)}
      </p>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-left text-[15px] font-semibold tabular-nums transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {frenchSpaces(o.text)}
          </button>
        ))}
      </div>
      <div aria-live="polite">
        {chosen && (
          <Note live={false} tone={chosen.correct ? 'success' : 'error'}>
            <span data-testid="ls-feedback">
              {frenchSpaces(
                chosen.correct
                  ? `Exact. ${q.explain}`
                  : `${chosen.hint ?? ''} La bonne réponse : ${right.text}. ${q.explain}`,
              )}
            </span>
          </Note>
        )}
      </div>
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

type Mode = 'monty' | 'geom' | 'poisson' | 'quiz';

export function LawSimTool({
  level = null,
  rng = Math.random,
  seed,
}: {
  /**
   * niveau : 1re, MPSI/PTSI = Monty Hall ; Tle (maths complémentaires) + loi
   * géométrique ; MP/PT (et niveau inconnu) + Poisson
   */
  level?: string | null;
  /** tirages des simulations (tests) */
  rng?: () => number;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  // MPSI/PTSI : univers finis, ni loi géométrique ni loi de Poisson
  const geom = rank === null || rank === 7 || rank === 9;
  const poisson = rank === null || rank === 9;
  // ouvert depuis une carte « Loi géométrique » : on commence par elle
  const [mode, setMode] = useState<Mode>(geom ? 'geom' : 'monty');
  const options: Array<{ value: Mode; label: string }> = [
    { value: 'monty', label: 'Monty Hall' },
    ...(geom ? [{ value: 'geom' as const, label: 'G(p)' }] : []),
    ...(poisson ? [{ value: 'poisson' as const, label: 'P(λ)' }] : []),
    { value: 'quiz', label: 'Défi' },
  ];
  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Partie de l’outil" value={mode} options={options} onChange={setMode} />
      <div hidden={mode !== 'monty'}>
        <MontyPanel rng={rng} />
      </div>
      {geom && (
        <div hidden={mode !== 'geom'}>
          <GeomPanel rng={rng} tle={rank === 7} />
        </div>
      )}
      {poisson && (
        <div hidden={mode !== 'poisson'}>
          <PoissonPanel />
        </div>
      )}
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} themes={quizThemes(rank)} />
      </div>
    </div>
  );
}
